import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { AssessmentResult } from './scoring';
import { formatDate } from './dates';

interface OrganizationData {
  name: string;
  location: string;
  size: string;
  denomination?: string;
  contactRole: string;
  contactName: string;
  contactEmail: string;
}

export async function generatePDF(
  results: AssessmentResult,
  organizationData: OrganizationData,
  contentElement: HTMLElement
): Promise<void> {
  try {
    // Create PDF document
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    // Set margins (1 inch = 25.4mm)
    const margin = 35; // Increased margin for better readability
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const contentWidth = pageWidth - (2 * margin);

    // Set metadata
    const reportDate = formatDate(new Date());
    const reportTitle = `${organizationData.name} - Operations Assessment Report`;

    pdf.setProperties({
      title: reportTitle,
      subject: 'Church Operations Assessment Report',
      author: 'ShepherdOps',
      keywords: 'church, operations, assessment',
      creator: 'ShepherdOps Assessment Tool',
      creationDate: new Date()
    });

    // Configure fonts and styling
    pdf.setFont('helvetica');
    const fonts = {
      header: { size: 16, style: 'bold' },
      subheader: { size: 14, style: 'bold' },
      body: { size: 11, style: 'normal' },
      footer: { size: 9, style: 'normal' }
    };

    const maxLineWidth = contentWidth * 2.83465; // Convert mm to points (1mm = 2.83465pt)

    // Enhanced canvas capture settings
    const canvas = await html2canvas(contentElement, {
      scale: 2, // Reduced scale for better fit
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: 800, // Fixed width to ensure consistent scaling
      onclone: (clonedDoc) => {
        // Preserve score circle styling
        const scoreCircle = clonedDoc.querySelector('.inline-flex.items-center.justify-center.w-40.h-40');
        if (scoreCircle instanceof HTMLElement) {
          scoreCircle.style.backgroundColor = '#EEF2FF'; // bg-indigo-100
          scoreCircle.style.borderRadius = '9999px'; // rounded-full
        }

        // Ensure score text is visible
        const scoreText = clonedDoc.querySelector('.text-4xl.font-bold.text-indigo-600');
        if (scoreText instanceof HTMLElement) {
          scoreText.style.color = '#4F46E5'; // text-indigo-600
          scoreText.style.fontSize = '2.25rem'; // text-4xl
          scoreText.style.fontWeight = '700'; // font-bold
        }

        // Preserve progress bars
        const progressBars = clonedDoc.querySelectorAll('.bg-indigo-600.h-2\\.5.rounded-full');
        progressBars.forEach(bar => {
          if (bar instanceof HTMLElement) {
            bar.style.backgroundColor = '#4F46E5'; // bg-indigo-600
            bar.style.height = '0.625rem'; // h-2.5
            bar.style.borderRadius = '9999px'; // rounded-full
          }
        });

        // Ensure all fonts are loaded
        const fontElements = clonedDoc.querySelectorAll('*');
        fontElements.forEach(el => {
          if (el instanceof HTMLElement) {
            el.style.fontDisplay = 'block';
            // Adjust font sizes for better fit
            const currentSize = parseFloat(window.getComputedStyle(el).fontSize);
            if (currentSize > 24) {
              el.style.fontSize = '24px';
            }
          }
        });
      }
    });

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // Calculate scaling to fit within margins
    let scale = contentWidth / canvasWidth;
    const scaledHeight = canvasHeight * scale;

    const contentAreaHeight = pageHeight - (2 * margin);
    
    // Adjust scale if content is too tall
    if (scaledHeight > contentAreaHeight) {
      scale = scale * (contentAreaHeight / scaledHeight);
    }
    const imgData = canvas.toDataURL('image/jpeg', 1.0);

    // Add content to single page
    // Add title
    pdf.setFontSize(fonts.header.size);
    pdf.setFont('helvetica', fonts.header.style);
    pdf.setTextColor(0, 0, 0);
    pdf.text(reportTitle, pageWidth / 2, margin, { align: 'center' });

    // Add main content with offset for title
    pdf.addImage(
      imgData,
      'JPEG',
      margin,
      margin + 10, // Add space for title
      contentWidth,
      Math.min(scaledHeight, contentAreaHeight - 20) // Adjust for title space
    );

    // Enhanced footer with page numbers and timestamp
    pdf.setFontSize(fonts.footer.size);
    pdf.setTextColor(128, 128, 128);
    
    const pageNumberY = pageHeight - (margin / 2);
    
    // Left footer: Generated date
    pdf.text(
      `Generated: ${reportDate}`,
      margin,
      pageNumberY
    );
    
    // Right footer: Organization name
    pdf.text(
      organizationData.name,
      pageWidth - margin,
      pageNumberY,
      { align: 'right' }
    );

    // Generate filename with date
    const safeOrgName = organizationData.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const dateStr = new Date().toISOString().split('T')[0];
    pdf.save(`${safeOrgName}_assessment_${dateStr}.pdf`);

  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF report. Please try again.');
  }
}